import { useDelivery } from 'core/logic/delivery/delivery.hook'
import { analyticsEvent } from 'core/modules/analytics/events'
import { doClaimDelivery, doOpen } from 'core/modules/firebase/service'
import { TO_MY_COLLECTION, TO_TRACKING_PAGE } from 'core/modules/router'
import {
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { messages } from './OpeningDrop.data'
import OpeningDropView from './OpeningDrop.view'
import { useJwtWalletRequiredEffect } from 'context/Profile'
import { DeliveryType } from 'core/logic/delivery/delivery.types'

const TEXT_DURATION = 10000

const OpeningDropLogic = ({ delivery }: { delivery?: DeliveryType }) => {
  const history = useHistory()
  const goToCollection = () => history.push(TO_MY_COLLECTION())
  const { deliveryId } = useParams<{ deliveryId: string }>()
  const drop = delivery?.offer
  const [assets, setAssets] = useState<any>([])
  const [closeModalOpen, setCloseModalOpen] = useState(false)
  const [errorModalOpen, setErrorModalOpen] = useState(false)
  const handleCloseModalOpen = () => setCloseModalOpen(true)
  const handleCloseModalClose = () => setCloseModalOpen(false)
  const handleErrorModalOpen = () => setErrorModalOpen(true)
  const handleErrorModalClose = () => goToCollection()

  const [opening, setOpening] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [curText, setCurText] = useState(0)

  const openPack = useCallback(async () => {
    try {
      if (
        !opening &&
        ['CLOSED', 'UNCLAIMED'].includes(delivery?.status || '')
      ) {
        setOpening(true)
        if (delivery) analyticsEvent.openPack({ pack: delivery })
        if (delivery?.status === 'CLOSED') {
          await doOpen({
            deliveryId,
            tenantId: process.env.REACT_APP_TENANT_IDENTITY_ID,
          })
        }
        if (delivery?.status === 'UNCLAIMED') {
          await doClaimDelivery(deliveryId)
        }
      }
    } catch (error) {
      console.error(error)
      handleErrorModalOpen()
    }
  }, [opening, delivery, deliveryId])

  const onOpenAssets = useCallback(() => {
    switch (delivery?.status) {
      case 'OPEN':
        setAssets(delivery?.assets)
        setIsOpen(true)
        break
      case 'MINED':
        setAssets([drop?.asset])
        setIsOpen(true)
        break
      case 'CLAIMED':
        setAssets([delivery?.asset])
        setIsOpen(true)
        break
    }
  }, [delivery?.status, drop?.asset, delivery?.assets])

  useJwtWalletRequiredEffect(() => {
    openPack()
  }, [openPack])

  useEffect(() => {
    onOpenAssets()
  }, [onOpenAssets])

  useEffect(() => {
    const textIntervalId = setInterval(() => {
      setCurText((curText + 1) % messages.length)
    }, TEXT_DURATION)
    return () => {
      clearTimeout(textIntervalId)
    }
  }, [curText])

  return (
    <OpeningDropView
      delivery={delivery}
      drop={drop}
      assets={assets}
      onBackIntent={
        delivery?.status === 'CLOSED' ? handleCloseModalOpen : goToCollection
      }
      onStay={handleCloseModalClose}
      backModalState={closeModalOpen}
      onBack={goToCollection}
      messages={messages}
      messageIndex={curText}
      isDropOpen={isOpen}
      errorModal={errorModalOpen}
      errorModalCloseHandler={handleErrorModalClose}
      onGoToCollection={goToCollection}
      onGoToTracking={() => history.push(TO_TRACKING_PAGE(deliveryId))}
    />
  )
}

export default function OpeningDropLogicWrapper() {
  const { deliveryId } = useParams<{ deliveryId: string }>()
  const { delivery } = useDelivery({ deliveryId })
  const ScreenWrapper = useMemo<
    ({ children }: PropsWithChildren) => ReactElement
  >(() => {
    switch (delivery?.type) {
      default:
        return ({ children }) => children as ReactElement
    }
  }, [delivery?.type])

  if (!delivery) return null
  return (
    <ScreenWrapper>
      <OpeningDropLogic delivery={delivery} />
    </ScreenWrapper>
  )
}
